import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './i18n';
import './school.css';
import { SchoolAuthProvider, useSchoolAuth } from './auth/SchoolAuthContext';
import AppLayout from './components/AppLayout';
import LoadingState from './design/LoadingState';
import LoginPage from './pages/LoginPage';
import StudentHomePage from './pages/StudentHomePage';
import TeacherClassPage from './pages/TeacherClassPage';
import TeacherStudentPage from './pages/TeacherStudentPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import { isHebrew } from './utils/pasuk';
import type { Teacher } from './types';

interface TeacherShellProps {
  teacher: Teacher;
}

const TeacherShell: React.FC<TeacherShellProps> = ({ teacher }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <AppLayout
      userName={teacher.name || teacher.email}
      userSubtitle={t('school_teacher_role')}
      navItems={[
        {
          key: 'class',
          label: t('school_nav_class_progress'),
          icon: 'users',
          active: location.pathname === '/' || location.pathname.startsWith('/students'),
          onClick: (): void => navigate('/'),
        },
      ]}
    >
      <Routes>
        <Route path="/" element={<TeacherClassPage teacher={teacher} />} />
        <Route path="/students/:studentId" element={<TeacherStudentPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppLayout>
  );
};

const AppContent: React.FC = () => {
  const { session, loading } = useSchoolAuth();
  const { i18n } = useTranslation();

  // Keep <html dir/lang> in sync so RTL layout and the Tailwind rtl:/ltr: variants follow the language.
  useEffect(() => {
    const lang = i18n.language || 'he';
    document.documentElement.dir = isHebrew(lang) ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
    document.title = isHebrew(lang) ? 'קול תורה · בית ספר' : 'Kol Torah · School';
  }, [i18n.language]);

  if (loading) return <LoadingState fullScreen />;

  if (!session) {
    return (
      <Routes>
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  switch (session.role) {
    case 'student':
      return (
        <Routes>
          <Route
            path="*"
            element={
              <StudentHomePage
                schoolId={session.schoolId}
                classId={session.classId}
                studentId={session.studentId}
              />
            }
          />
        </Routes>
      );
    case 'teacher':
      return <TeacherShell teacher={session.teacher} />;
    default:
      return <UnauthorizedPage email={session.email} />;
  }
};

const App: React.FC = () => (
  <SchoolAuthProvider>
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppContent />
    </Router>
  </SchoolAuthProvider>
);

export default App;
